/**
 * usePincodeAutoFill — resolves pincode/zipcode to City, State, Country
 * Supports: India (6-digit), USA (5-digit), UK, Canada, Australia, Germany, UAE + more
 * Primary free APIs:  postalpincode.in (India)  |  zippopotam.us (worldwide)
 */
import { useState, useCallback, useRef } from 'react';

// Detect probable country from postal code format
function detectCountry(code) {
  const c = code.trim().toUpperCase();
  if (/^\d{6}$/.test(c)) return 'IN';              // India
  if (/^\d{5}(-\d{4})?$/.test(c)) return 'US';     // USA
  if (/^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/.test(c)) return 'GB'; // UK
  if (/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/.test(c)) return 'CA';           // Canada
  if (/^\d{4}$/.test(c)) return 'AU';              // Australia (4-digit)
  if (/^\d{5}$/.test(c) && parseInt(c) >= 10000) return 'DE';       // Germany heuristic
  if (/^\d{5}$/.test(c)) return 'US';              // default 5-digit → US
  return null;
}

async function lookupIndia(pincode) {
  const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`, {
    signal: AbortSignal.timeout(5000)
  });
  if (!res.ok) return null;
  const data = await res.json();
  if (!Array.isArray(data) || data[0]?.Status !== 'Success') return null;
  const po = data[0].PostOffice?.[0];
  if (!po) return null;
  return {
    city: po.Division || po.Block || po.Name,
    district: po.District,
    state: po.State,
    country: 'India',
  };
}

async function lookupZippopotam(countryCode, zipcode) {
  const res = await fetch(`https://api.zippopotam.us/${countryCode.toLowerCase()}/${zipcode}`, {
    signal: AbortSignal.timeout(5000)
  });
  if (!res.ok) return null;
  const data = await res.json();
  const place = data.places?.[0];
  if (!place) return null;
  return {
    city: place['place name'],
    state: place['state'],
    country: data['country'],
  };
}

export function usePincodeAutoFill() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detected, setDetected] = useState(null); // { city, state, country }
  const debounceRef = useRef(null);

  const lookup = useCallback((rawValue, onAutoFill) => {
    const value = rawValue?.trim() || '';
    setError('');
    setDetected(null);

    // Clear previous debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Minimum length before attempting lookup
    const stripped = value.replace(/\s/g, '');
    if (stripped.length < 4) return;

    debounceRef.current = setTimeout(async () => {
      const country = detectCountry(value);
      if (!country) return; // unrecognised format — user types manually

      setLoading(true);
      try {
        let result = null;
        if (country === 'IN') {
          result = await lookupIndia(stripped);
        } else {
          result = await lookupZippopotam(country, stripped);
        }

        if (result) {
          setDetected(result);
          onAutoFill?.({
            city: result.city || '',
            state: result.state || '',
            country: result.country || '',
          });
        } else {
          setError('Pincode not found — please enter city/state manually');
        }
      } catch {
        setError('Could not auto-detect location');
      } finally {
        setLoading(false);
      }
    }, 600); // 600ms debounce
  }, []);

  return { lookup, loading, error, detected };
}
